import React, { useState, type PropsWithChildren } from 'react';
import { Formik } from 'formik';
import AuthorizationLayout from '../components/AuthorizationLayout';
import { useTranslation } from 'react-i18next';
import Form from 'react-bootstrap/Form';
import googleSvg from '../../../../assets/images/svgs/google.svg';
import facebookSvg from '../../../../assets/images/svgs/facebook.svg';
import eyeSVG from '../../../../assets/images/svgs/eye.svg';
import hideEyeSVG from '../../../../assets/images/svgs/hide-eye.svg';
import Button from '../../../../components/primitives/button';
import '../styles.scss';
import { splitEmailOrPhone } from '../../application/splitEmailOrPhone';
import { readApiError } from '../../application/readApiError';
import { useSignUpMutation } from '../../infrastructure/api/auth.api';
import { paths } from '../../../../app/routes/paths';
import { showErrorToast } from '../../../../app/components/SnackBar';

const SignUp = (props: PropsWithChildren) => {
  const { t } = useTranslation();
  const [signUp] = useSignUpMutation();
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (data, { setSubmitting }) => {
    const identity = splitEmailOrPhone(data.emailOrPhone);
    if (!identity) {
      setSubmitting(false);
      showErrorToast(t('form.invalid_email_or_phone'));
      return;
    }

    try {
      await signUp({
        emailOrPhone: data.emailOrPhone.trim(),
        password: data.password,
        role: data.role,
      }).unwrap();
    } catch (error) {
      const apiError = readApiError(error);
      showErrorToast(apiError.message || t('form.server_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthorizationLayout {...props}>
      <div className={'sign-up border br-16 p-16 mb-4 mt-3'}>
        <div className={`auth-data-wrapper`}>
          <div className="block-title fs-3 mb-4 mt-3">{t('sign_up.title')}</div>
          <Formik<any>
            initialValues={{ emailOrPhone: '', password: '', role: 'student' }}
            validate={(values) => {
              const errors: any = {};
              if (!values.emailOrPhone) {
                errors.emailOrPhone = t('form.required');
              } else if (
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.emailOrPhone) &&
                !/^\+[1-9]\d{1,14}$/.test(values.emailOrPhone)
              ) {
                errors.emailOrPhone = t('form.invalid_email_or_phone');
              }
              if (!values.password) errors.password = t('form.required');
              else if (values.password.length < 8) {
                errors.password = t('form.invalid_password', {
                  defaultValue: 'Password must be at least 8 characters',
                });
              }
              return errors;
            }}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="custom-form-group auth-form-group">
                      <Form.Control
                        id="emailOrPhone"
                        name="emailOrPhone"
                        placeholder="Email/phone"
                        className={`custom-form-control auth-form-control ${errors.emailOrPhone && touched.emailOrPhone ? 'is-invalid' : ''}`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.emailOrPhone}
                      ></Form.Control>
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="custom-form-group auth-form-group">
                      <Form.Control
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        className={`custom-form-control auth-form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                      ></Form.Control>
                      <img
                        className={'hide-icon'}
                        src={showPassword ? eyeSVG : hideEyeSVG}
                        alt="Toggle visibility"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="form-group">
                      <Form.Label htmlFor="role">{t('sign_up.role')}</Form.Label>
                      <Form.Select
                        aria-label=""
                        className={`custom-form-select ${errors.role && touched.role ? 'is-invalid' : ''}`}
                        id="role"
                        name="role"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.role}
                      >
                        <option key="0" value="student">
                          {t('sign_up.role_client')}
                        </option>
                        <option key="1" value="mentor">
                          {t('sign_up.role_mentor')}
                        </option>
                      </Form.Select>
                    </div>
                  </div>
                </div>
                <div className="d-flex mt-2 mb-4 justify-content-center">
                  <button
                    type="submit"
                    className="btn btn-primary cursor-pointer"
                    disabled={isSubmitting}
                  >
                    <span className={`submit-btn-text`}> {t('sign_up.registry')}</span>
                  </button>
                </div>
                <div className={`custom-hr`}></div>
                <div className="d-flex mt-4 justify-content-center">
                  {t('sign_in.login_with_services')}
                </div>
                <div className="sign_up_service_block mt-3">
                  <Button
                    className="non-border-btn"
                    iconSvg={<img src={googleSvg} alt="icon" />}
                  ></Button>
                  <Button
                    className="non-border-btn"
                    iconSvg={<img src={facebookSvg} alt="icon" />}
                  ></Button>
                </div>
                <div className="d-flex mt-3 mb-1 justify-content-center auth-quest">
                  {t('sign_up.account')}
                  &nbsp;
                  <a className={'link-underline td-n ml-6'} href={paths.signIn}>
                    {t('sign_up.sign_in')}
                  </a>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </AuthorizationLayout>
  );
};

export default SignUp;
